import React, { SVGProps } from "react";
import styles from "./badge.module.scss";
import {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
} from "@kunduz/utility-polymorphic-component";
import classNames from "classnames";

type BadgeSize = "sm" | "md" | "lg";
type BadgeColor =
  | "blue"
  | "red"
  | "green"
  | "gray"
  | "pink"
  | "purple"
  | "yellow";
type BadgeVariant =
  | "regular"
  | "dot"
  | "icon-leading"
  | "icon-trailing"
  | "icon"
  | "avatar";

type BadgeProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<
  C,
  {
    size: BadgeSize | "lg";
    color: BadgeColor | "blue";
    variant: BadgeVariant | "regular";
    outline?: boolean;
  }
>;

type BadgeComponent = <C extends React.ElementType = "a">(
  props: BadgeProps<C>,
) => React.ReactElement | null | any;

export const Badge: BadgeComponent = React.forwardRef(
  <C extends React.ElementType = "div">(
    {
      as,
      children,
      variant,
      size,
      color,
      outline,
      className,
      ...rest
    }: BadgeProps<C>,
    ref?: PolymorphicRef<C>,
  ) => {
    const Component = as || "div";

    return (
      <Component
        ref={ref}
        {...rest}
        className={classNames(
          styles.base,
          styles[`size-${size}`],
          styles[`color-${color}`],
          styles[`variant-${variant}`],
          { [styles["outline"]]: outline },
          className,
        )}
      >
        {children}
      </Component>
    );
  },
);

//@ts-ignore
Badge.displayName = "Badge";

export const BadgeDot = (props: SVGProps<any>) => {
  return (
    <svg
      width="8"
      height="8"
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <circle cx="4" cy="4" r="3" />
    </svg>
  );
};
